import ConnectionHandlerPlus from "relay-connection-handler-plus"
import {
  DeleteTrainingSourceButtonMutation,
  TrainingSourceKind,
} from "@/admin/training-sources/__generated__/DeleteTrainingSourceButtonMutation.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoButtonSkeleton } from "@disco-ui"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import DiscoWarningModal from "@disco-ui/modal/DiscoWarningModal"
import useDisclosure from "@utils/hook/useDisclosure"
import { TestIDProps } from "@utils/typeUtils"
import { ConnectionHandler, graphql, useMutation } from "react-relay"

interface DeleteTrainingSourceButtonProps extends TestIDProps {
  trainingSourceId: GlobalID
  children: OverridableDiscoButtonChildren
  kind?: TrainingSourceKind
}

function DeleteTrainingSourceButton({
  trainingSourceId,
  testid = "DeleteTrainingSourceButton",
  children = "Delete",
  kind = "web_crawl",
}: DeleteTrainingSourceButtonProps) {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const [commit, isDeleting] = useMutation<DeleteTrainingSourceButtonMutation>(graphql`
    mutation DeleteTrainingSourceButtonMutation(
      $id: ID!
      $kinds: [TrainingSourceKind!]
      $connections: [ID!]!
    ) {
      response: deleteTrainingSource(id: $id) {
        deletedSourceId @deleteEdge(connections: $connections) @deleteRecord
        organization {
          trainingDataCount(kinds: $kinds)
          trainingSources(kinds: $kinds) {
            totalCount
          }
        }
      }
    }
  `)

  const source = kind === "web_crawl" ? "link" : "knowledge base"

  return (
    <>
      <OverridableDiscoButton onClick={onOpen}>{children}</OverridableDiscoButton>
      <DiscoWarningModal
        testid={`${testid}Modal`}
        isOpen={isOpen}
        onClose={onClose}
        classes={{
          title: classes.header,
        }}
        title={`Delete this ${source}?`}
        description={`Are you sure you want to permanently delete this ${source}? There is no way to undo this action.`}
        confirmationButtonProps={{
          onClick: deleteTrainingSource,
          shouldDisplaySpinner: isDeleting,
          children: "Yes, delete it",
        }}
        modalContentLabel={"Delete Training Source Dialog"}
      />
    </>
  )

  function deleteTrainingSource() {
    commit({
      variables: {
        id: trainingSourceId,
        kinds: [kind],
        connections: [
          ConnectionHandler.getConnectionID(
            activeOrganization.id,
            "TrainingSourceList_trainingSources"
          ),
        ],
      },
      updater: (store) => {
        const orgRecord = store.get(activeOrganization.id)
        if (!orgRecord) return
        ConnectionHandlerPlus.getConnections(
          orgRecord,
          "TrainingSourceTable_trainingSources"
        ).forEach((connection) =>
          ConnectionHandlerPlus.deleteNode(connection, trainingSourceId)
        )
      },
    })
    onClose()
  }
}

const useStyles = makeUseStyles((theme) => ({
  header: {
    gap: theme.spacing(1),
  },
}))

export default Relay.withSkeleton({
  skeleton: () => <DiscoButtonSkeleton width={"100%"} />,
  component: DeleteTrainingSourceButton,
})
